import { useCallback, useContext, useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useLanguage } from '@languages/index';
import { GuidelineRuleDtoEnums } from 'api/repositories/guidelineRules/guidelineRules.types';
import { GUIDELINE_RULE_DTO_ENUMS } from 'api/repositories/guidelineRules/guidelineRules.constants';
import { GetOptionTranslationMap } from 'constants/guidelineRules';
import Checkbox from 'components/checkbox';
import { CampaignFormContext } from 'pages/campaigns/campaignForm/campaignForm.context';
import { CampaignFormStepTwoContext } from '../../../../../campaignFormStepTwo.context';
import CampaignFormField from '../../../../../../campaignFormFieldGroup/components/campaignFormField/campaignFormField';

interface BaseProhibitionRule {
  value: string;
  order: number;
}

interface ProhibitionsFieldProps {
  prohibitionsOptionNames: GuidelineRuleDtoEnums['PROHIBITIONS_OPTIONS'][];
}

const ProhibitionsField = ({
  prohibitionsOptionNames,
}: ProhibitionsFieldProps) => {
  const language = useLanguage();
  const languageTexts = { ...language.pages.campaigns };
  const { brand, guidelineRuleFormMethods } = useContext(CampaignFormContext);
  const { baseGuidelineRules } = useContext(CampaignFormStepTwoContext);

  const prohibitions = useWatch({
    control: guidelineRuleFormMethods.control,
    name: 'PROHIBITIONS',
  });

  const baseProhibitions: BaseProhibitionRule[] = useMemo(() => {
    return baseGuidelineRules?.reduce((acc, rule) => {
      if (
        rule.groupName === GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS &&
        prohibitionsOptionNames.includes(
          rule.value as GuidelineRuleDtoEnums['PROHIBITIONS_OPTIONS'],
        )
      ) {
        acc.push({ value: rule.value, order: rule.order });
      }

      return acc;
    }, []);
  }, [baseGuidelineRules]);

  const onChange = useCallback((rule: BaseProhibitionRule) => {
    const tempProhibitions = guidelineRuleFormMethods.getValues('PROHIBITIONS');
    const foundIndex = tempProhibitions.findIndex(
      (tempProhibition) => tempProhibition.value === rule.value,
    );

    if (foundIndex < 0) {
      tempProhibitions.push(rule);
    } else {
      tempProhibitions.splice(foundIndex, 1);
    }

    tempProhibitions.sort((a, b) => a.order - b.order);

    guidelineRuleFormMethods.setValue('PROHIBITIONS', tempProhibitions);
  }, []);

  return (
    <CampaignFormField
      as="ul"
      wrapperClassName="!gap-[12px]"
      label={
        brand.isWholesale
          ? languageTexts.create_prohibitions_check_1
          : languageTexts.create_prohibitions_check_2
      }
    >
      {baseProhibitions?.map((baseProhibition, index) => {
        const checked = prohibitions?.some(
          (prohibition) => prohibition.value === baseProhibition.value,
        );

        return (
          <li
            key={`prohibitions-field-${index}`}
            className="flex flex-row items-center"
          >
            <Checkbox
              className="select-none"
              textClassName="select-none"
              text={GetOptionTranslationMap(
                GUIDELINE_RULE_DTO_ENUMS.GROUP_NAME.PROHIBITIONS,
                baseProhibition.value,
              )}
              checked={checked}
              onClick={() => onChange(baseProhibition)}
            />

            {baseProhibition.value ===
              GUIDELINE_RULE_DTO_ENUMS.PROHIBITIONS_OPTIONS.INDOORS && (
              <div className="ml-8 text-xs text-[#959595]">
                {languageTexts.create_stepTwo_text37}
              </div>
            )}
          </li>
        );
      })}
    </CampaignFormField>
  );
};

export default ProhibitionsField;
